import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL, ENVIRONMENT, IS_PROD_LIKE_ENVIRONMENT } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import type { BannerResponse } from '@/types/Banner'

const fetchBannerFromS3 = async (): Promise<BannerResponse> => {
  if (!IS_PROD_LIKE_ENVIRONMENT || ENVIRONMENT === 'knox-prod') {
    return { message: '', show: false }
  }

  const res: Response = await fetch(
    `https://sweetspot-status.s3.us-east-2.amazonaws.com/banner.json`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  const res_json: BannerResponse = await res.json()
  return res_json
}

const fetchBanner = async (
  accessToken: string | null | undefined,
): Promise<BannerResponse> => {
  if (!accessToken) {
    return { message: '', show: false }
  }
  const res: Response = await fetch(`${API_URL}/api/v1/banner/`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  const res_json: BannerResponse = await res.json()
  return res_json
}

export const useBanner = () => {
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryFn: () =>
      ENVIRONMENT === 'knox-prod'
        ? fetchBanner(accessToken ?? null)
        : fetchBannerFromS3(),
    queryKey: [
      'banner',
      ENVIRONMENT,
      ENVIRONMENT === 'knox-prod' ? accessToken : null,
    ],
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })
}
